import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { AlertCircle, Check, LogIn } from 'lucide-react'
import SEO from '../components/SEO'
import { getReportConfig, updateReportConfig } from '../actions/reportActions'
import { REPORT_CONFIG_UPDATE_RESET } from '../constants/reportConstants'
import './ReportConfigScreen.css'

// "chiefComplaint" rendered as "ChiefComplaint" before — the old formatter
// capitalised the first letter but never split the camelCase.
const humanise = (key) =>
  key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim()

const GROUPS = [
  { id: 'soap', name: 'SOAP' },
  { id: 'chart', name: 'CHART' },
  { id: 'dchart', name: 'DCHART' },
]

function ReportConfigScreen() {
  const dispatch = useDispatch()

  const userLogin = useSelector((state) => state.userLogin)
  const { userInfo } = userLogin

  const reportConfigGet = useSelector((state) => state.reportConfigGet)
  const { loading, error, config } = reportConfigGet

  const reportConfigUpdate = useSelector((state) => state.reportConfigUpdate)
  const {
    loading: loadingUpdate,
    error: errorUpdate,
    success: successUpdate,
  } = reportConfigUpdate

  const [soapConfig, setSoapConfig] = useState({
    subjective: '',
    objective: '',
    assessment: '',
    plan: '',
  })
  const [chartConfig, setChartConfig] = useState({
    chiefComplaint: '',
    history: '',
    assessment: '',
    rx: '',
    transport: '',
  })
  const [dchartConfig, setDchartConfig] = useState({
    dispatch: '',
    chiefComplaint: '',
    history: '',
    assessment: '',
    rx: '',
    transport: '',
  })

  useEffect(() => {
    if (!userInfo) return
    if (successUpdate || !config) {
      if (successUpdate) {
        dispatch({ type: REPORT_CONFIG_UPDATE_RESET })
      }
      dispatch(getReportConfig())
    }
  }, [dispatch, userInfo, successUpdate, config])

  useEffect(() => {
    if (!config) return
    setSoapConfig(config.soap || { subjective: 'S:', objective: 'O:', assessment: 'A:', plan: 'P:' })
    setChartConfig(
      config.chart || { chiefComplaint: 'C:', history: 'H:', assessment: 'A:', rx: 'R:', transport: 'T:' }
    )
    setDchartConfig(
      config.dchart || {
        dispatch: 'D:',
        chiefComplaint: 'C:',
        history: 'H:',
        assessment: 'A:',
        rx: 'R:',
        transport: 'T:',
      }
    )
  }, [config])

  const values = { soap: soapConfig, chart: chartConfig, dchart: dchartConfig }
  const setters = { soap: setSoapConfig, chart: setChartConfig, dchart: setDchartConfig }

  const handleChange = (groupId) => (event) => {
    setters[groupId]({ ...values[groupId], [event.target.name]: event.target.value })
  }

  const handleSave = () => {
    dispatch(updateReportConfig({ soap: soapConfig, chart: chartConfig, dchart: dchartConfig }))
  }

  const seo = (
    <SEO
      title="Report Configuration - PCR Assistant"
      description="Configure your report labels and formats."
      canonicalUrl="/report-config"
    />
  )

  if (!userInfo) {
    return (
      <>
        {seo}
        <div className="rc">
          <div className="rc__wrap">
            <header className="rc__head">
              <h1 className="rc__title">Report configuration</h1>
            </header>
            <div className="rc__gate">
              <p>You need to be signed in to manage your report configuration.</p>
              <Link className="rc__btn rc__btn--primary" to="/login">
                <LogIn size={16} aria-hidden="true" />
                Sign in
              </Link>
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      {seo}
      <div className="rc">
        <div className="rc__wrap">
          <header className="rc__head">
            <h1 className="rc__title">Report configuration</h1>
            <p className="rc__lede">
              Define the display labels for your SOAP, CHART, and DCHART reports.
            </p>
          </header>

          {error && (
            <p className="rc__error" role="alert">
              <AlertCircle size={16} aria-hidden="true" />
              <span>{error}</span>
            </p>
          )}

          {errorUpdate && (
            <p className="rc__error" role="alert">
              <AlertCircle size={16} aria-hidden="true" />
              <span>{errorUpdate}</span>
            </p>
          )}

          {loading ? (
            <p className="rc__note">Loading your configuration…</p>
          ) : (
            <>
              {GROUPS.map((group) => (
                <fieldset className="rc__group" key={group.id}>
                  <legend className="rc__legend">{group.name}</legend>
                  <div className="rc__fields">
                    {Object.keys(values[group.id]).map((key) => (
                      <div className="rc__field" key={key}>
                        <label className="rc__label" htmlFor={`${group.id}-${key}`}>
                          {humanise(key)}
                        </label>
                        <input
                          id={`${group.id}-${key}`}
                          className="rc__input"
                          type="text"
                          name={key}
                          value={values[group.id][key]}
                          onChange={handleChange(group.id)}
                        />
                      </div>
                    ))}
                  </div>
                </fieldset>
              ))}

              <div className="rc__actions">
                <button
                  type="button"
                  className="rc__btn rc__btn--primary"
                  onClick={handleSave}
                  disabled={loadingUpdate}
                >
                  {loadingUpdate ? 'Saving…' : 'Save settings'}
                </button>

                {successUpdate && (
                  <span className="rc__saved" role="status">
                    <Check size={16} aria-hidden="true" />
                    Settings saved
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}

export default ReportConfigScreen
